import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Column, Lookup } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, Label, SimpleItem, FormRef, EmptyItem } from "devextreme-react/form";
import Button from 'devextreme-react/button';
import IMessage from "../../models/IMessage";
import IGestionMessage from "../../models/IGestionMessage";
import IMessageFichier from "../../models/IMessageFichier";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import { useContextPopupPage } from "../../components/popups/PopupPage";
import PopupFichiersJoints, { IFichierRoute } from './popup-fichiers-joints';
import useEnum from '../../hooks/useEnum';
import useApi from '../../hooks/useApi';
import { useAuth } from '../../contexts/auth';
import Guid from '../../models/Guid';

interface IGestionMessagesProps {
  isModeSelection?: boolean,
}

const mapFichiersToRoutes = (fichiers: IMessageFichier[] | undefined): IFichierRoute[] => {
  if (!Array.isArray(fichiers)) {
    return [];
  }
  return fichiers.map((fichier) => ({
    id: fichier.idMessageFichier?.toString() || Guid.newTemporary().toString(),
    chemin: fichier.chemin || fichier.nomFichier || '',
    anglais: !!fichier.anglais,
  }));
};

const nomFichierFromChemin = (chemin: string) => {
  const parts = chemin.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || chemin;
};

export default function GestionMessages({ isModeSelection }: IGestionMessagesProps) {
  const messageFormDataSource = useCustomDataSource<IMessage, IGestionMessage>({
    path: "message",
    primaryKey: "idMessage",
    customReadByIdPath: "message/{key}/gestion",
  });
  const { setMainForm, selectedDataRow: selectedMessage, mode, setMode, isFieldModifier, setIsFieldModifier, onFieldDataChanged, sauvegarder } = messageFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { setSelectedValue } = useContextPopupPage();
  const [isPopupFichiersJointsOpen, setIsPopupFichiersJointsOpen] = useState(false);
  const [fichiersJoints, setFichiersJoints] = useState<IFichierRoute[]>([]);
  const fichiersJointsSavedRef = useRef<IFichierRoute[]>([]);
  const { editorOptions: messageTypeEditorOptions } = useEnum(undefined, "gestionMessageType", '200px', 1);
  const { editorOptions: positionMessageEditorOption } = useEnum(undefined, "gestionMessagePositionPage", '200px', 1);
  const { editorOptions: positionNoPageEditorOption } = useEnum(undefined, "gestionMessagePositionNumeroPage", '120px', 1);
  const { postData: postMessageFichiers } = useApi<IMessageFichier[]>("message");
  const { user } = useAuth();

  const applyFichierRoutes = useCallback((fichiers: IMessageFichier[] | undefined) => {
    const mapped = mapFichiersToRoutes(fichiers);
    setFichiersJoints(mapped);
    fichiersJointsSavedRef.current = mapped.map((f) => ({ ...f }));
  }, []);

  const renderListeFichiersButton = useMemo(() => {
    return () => (
      <Button text="Liste des fichiers…" onClick={() => setIsPopupFichiersJointsOpen(true)} />
    );
  }, []);

  const onSauvegarderClick = useCallback(async (e: { preventDefault?: boolean }) => {
    if (!sauvegarder) {
      return;
    }

    e.preventDefault = true;
    const idMessage = await sauvegarder(e);
    if (!idMessage) {
      return;
    }

    const saved = await postMessageFichiers({
      selectedDataRow: fichiersJoints.map((fichier) => ({
        idMessageFichier: new Guid(fichier.id),
        chemin: fichier.chemin,
        anglais: fichier.anglais,
        nomFichier: nomFichierFromChemin(fichier.chemin),
      })),
      idUsager: user?.idUsager,
    }, `message/${idMessage}/fichiersroutes`, true);

    const mapped = mapFichiersToRoutes(Array.isArray(saved) ? saved : []);
    setFichiersJoints(mapped);
    fichiersJointsSavedRef.current = mapped.map((f) => ({ ...f }));
  }, [sauvegarder, fichiersJoints, postMessageFichiers, user?.idUsager]);

  const onAnnulerClick = useCallback(() => {
    setFichiersJoints(fichiersJointsSavedRef.current.map((f) => ({ ...f })));
  }, []);

  const messageDefautTypeEditorOptions = useMemo(() => {
    return {
      text: "Message par défaut pour ce type"
    }
  }, []);

  const encadrerLogoInfoEditorOptions = useMemo(() => {
    return { text: "Encadrer le logo et informations" }
  }, []);
  const afficherLogoEditorOptions = useMemo(() => {
    return { text: "Afficher le logo" }
  }, []);
  const afficherEntrepriseEditorOptions = useMemo(() => {
    return { text: "Afficher le nom d'entreprise" }
  }, []);
  const entetePremierePageEditorOptions = useMemo(() => {
    return { text: "Afficher l'entête sur la première page du rapport seulement" }
  }, []);
  const afficherAdressePageEditorOptions = useMemo(() => {
    return { text: "Afficher l'adresse de l'entreprise" }
  }, []);
  const cacherPreposeEditorOptions = useMemo(() => {
    return { text: "Cacher le préposé" }
  }, []);
  const afficherNoTaxePageEditorOptions = useMemo(() => {
    return { text: "Afficher numéros de taxes" }
  }, []);
  const datesAuLongPageEditorOptions = useMemo(() => {
    return { text: "Utiliser le format des dates au long" }
  }, []);
  const cacherSignatureEditorOptions = useMemo(() => {
    return { text: "Cacher la signature" }
  }, []);
  const texteEditorOptions = useMemo(() => {
    return { height: 230 }
  }, []);
  const maxLengthOptions = useMemo(() => {
    return { maxLength: 100 }
  }, []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  });

  useEffect(() => {
    if (mode !== Mode.Lecture)
      formRef.current?.instance().getEditor("description")?.focus();
  }, [mode]);

  useEffect(() => {
    setIsPopupFichiersJointsOpen(false);
    applyFichierRoutes(selectedMessage?.fichierRoutes);
  }, [selectedMessage?.idMessage, applyFichierRoutes]);

  useEffect(() => {
    if (setSelectedValue && selectedMessage?.idMessage) {
      setSelectedValue(selectedMessage?.idMessage)
    }
  }, [selectedMessage]);

  const isCourriel = selectedMessage?.typeMessage === 'e';
  const isFacture = selectedMessage?.typeMessage === 'f';
  return (
    <FormListBase key={"frm-messages"} data={messageFormDataSource}>
      <FormListBaseGrid
        dataSource={messageFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier)
            setMode(Mode.Lecture);
          if (setSelectedValue && e.row?.data)
            setSelectedValue(e.row.data);
        }}
        onRowDoubleClick={(e: any) => {
          if (setSelectedValue && e.data.idMessage)
            setSelectedValue(e.data.idMessage)
        }}>
        <Column key={"grid-col-type-message"} dataField={"typeMessage"} caption="Type" allowHiding={false}>
          <Lookup {...messageTypeEditorOptions} />
        </Column>
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar
        headerText={`${selectedMessage?.description || ''}`}
        sauvegarderOptions={{ disabled: !isFieldModifier, click: onSauvegarderClick }}
        annulerOptions={{ disabled: !isFieldModifier, click: onAnnulerClick }}
        effacerOptions={{ disabled: !isFieldModifier }}
      />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          <TabItem title="Informations">
            {isPopupFichiersJointsOpen ? (
              <PopupFichiersJoints
                fichiers={fichiersJoints}
                readOnly={mode === Mode.Lecture}
                onClose={() => setIsPopupFichiersJointsOpen(false)}
                onConfirm={(fichiers) => {
                  setFichiersJoints(fichiers);
                  setIsFieldModifier(true);
                }}
              />
            ) : null}
            <Form ref={formRef} style={{ height: "100%", overflow: "auto", paddingBottom: "1rem" }} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedMessage || {} as IMessage} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem colCount={isCourriel ? 2 : 3}>
                <SimpleItem key={"frm-type-message"} dataField={"typeMessage"} editorOptions={messageTypeEditorOptions} editorType={"dxSelectBox"}>
                  <Label text={"Type de message"} />
                </SimpleItem>
                {!isCourriel ? (
                  <SimpleItem key={"frm-fichiers-joints"} render={renderListeFichiersButton}>
                    <Label text={"Fichiers joints"} />
                  </SimpleItem>
                ) : null}
                <SimpleItem key={"frm-message-defaut-type"} dataField={"messageDefautType"} editorType={"dxCheckBox"} editorOptions={messageDefautTypeEditorOptions}>
                  <Label visible={false} />
                </SimpleItem>
              </GroupItem>
              <SimpleItem key={"frm-description"} dataField={"description"} editorOptions={maxLengthOptions} />
              <GroupItem caption={"Français"} alignItemLabels={false}>
                <SimpleItem key={"frm-titre-fr"} dataField={"titreFr"} editorOptions={maxLengthOptions}>
                  <Label text={"Titre"} />
                </SimpleItem>
              </GroupItem>
              <SimpleItem key={"frm-texte-fr"} dataField={"texteFr"} editorType={"dxTextArea"} editorOptions={texteEditorOptions}>
                <Label visible={false} />
              </SimpleItem>
              <GroupItem caption={"Anglais"} alignItemLabels={false}>
                <SimpleItem key={"frm-titre-en"} dataField={"titreEn"} editorOptions={maxLengthOptions}>
                  <Label text={"Titre"} />
                </SimpleItem>
              </GroupItem>
              <SimpleItem key={"frm-texte-en"} dataField={"texteEn"} editorType={"dxTextArea"} editorOptions={texteEditorOptions}>
                <Label visible={false} />
              </SimpleItem>
              {!isCourriel &&
                <GroupItem caption={"Options"}>
                  <GroupItem colCount={3}>
                    <SimpleItem key={"frm-encadrer-logo-info"} dataField={"encadrerLogoInfo"} editorType={"dxCheckBox"} editorOptions={encadrerLogoInfoEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem key={"frm-afficher-adresse"} dataField={"afficherAdresse"} editorType={"dxCheckBox"} editorOptions={afficherAdressePageEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem key={"frm-cacher-prepose"} dataField={"cacherPrepose"} editorType={"dxCheckBox"} editorOptions={cacherPreposeEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem colCount={3}>
                    <SimpleItem key={"frm-afficher-logo"} dataField={"afficherLogo"} editorType={"dxCheckBox"} editorOptions={afficherLogoEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem key={"frm-afficher-no-taxe"} dataField={"afficherNoTaxe"} editorType={"dxCheckBox"} editorOptions={afficherNoTaxePageEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                    <SimpleItem key={"frm-cacher-signature"} dataField={"cacherSignature"} editorType={"dxCheckBox"} editorOptions={cacherSignatureEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                  </GroupItem>
                  <GroupItem colCount={3}>
                    <SimpleItem key={"frm-afficher-entreprise"} dataField={"afficherEntreprise"} editorType={"dxCheckBox"} editorOptions={afficherEntrepriseEditorOptions}>
                      <Label visible={false} />
                    </SimpleItem>
                    {!isFacture
                      ? <SimpleItem key={"frm-dates-au-long"} dataField={"datesAuLong"} editorType={"dxCheckBox"} editorOptions={datesAuLongPageEditorOptions}>
                        <Label visible={false} />
                      </SimpleItem>
                      : <EmptyItem />
                    }
                    <EmptyItem />
                  </GroupItem>
                  <SimpleItem key={"frm-entete-premiere-page"} dataField={"entetePremierePage"} editorType={"dxCheckBox"} editorOptions={entetePremierePageEditorOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                  <SimpleItem key={"frm-position-message"} dataField={"positionMessage"} editorType={"dxSelectBox"} editorOptions={positionMessageEditorOption}>
                    <Label text={"Position du message"} />
                  </SimpleItem>
                  <SimpleItem key={"frm-position-no-page"}
                    dataField={"positionNoPage"}
                    editorType={"dxSelectBox"} editorOptions={positionNoPageEditorOption}>
                    <Label text={"Position du numéro de page"} />
                  </SimpleItem>
                </GroupItem>
              }
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedMessage || {} as IMessage} /></TabItem>
        </FormListBaseTab> : null
      }
    </FormListBase>
  );
}
